import { useState } from "react";
import { Globe, FileText, BookOpen, Images, Share2, Presentation, CheckCircle2 } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/modern/components/ui/dialog-shadcn";
import { Button } from "@/modern/components/ui/button-shadcn";
import { cn } from "@/modern/utils";
import { useCoverage } from "../context/CoverageContext";

const OUTPUT_TYPES = [
  {
    id: "webpage",
    label: "Web Page",
    description: "SEO-optimized pillar page for this problem hub",
    icon: Globe,
  },
  {
    id: "case_study",
    label: "Case Study",
    description: "Single-task narrative with before/after evidence",
    icon: BookOpen,
  },
  {
    id: "pdf",
    label: "PDF / Deck",
    description: "Downloadable presentation or marketing collateral",
    icon: FileText,
  },
  {
    id: "gallery",
    label: "Gallery",
    description: "Visual evidence gallery organized by bucket roles",
    icon: Images,
  },
  {
    id: "social",
    label: "Social Post",
    description: "Short-form content for social media channels",
    icon: Share2,
  },
  {
    id: "report",
    label: "Report",
    description: "Technical inspection or assessment document",
    icon: Presentation,
  },
] as const;

export const GenerateOutputDialog = () => {
  const { isGenerateOpen, setIsGenerateOpen, selectedHub } = useCoverage();
  const [selected, setSelected] = useState<string | null>(null);

  const handleOpenChange = (open: boolean) => {
    setIsGenerateOpen(open);
    if (!open) setSelected(null);
  };

  const handleGenerate = () => {
    if (!selected || !selectedHub) return;
    const _outputType = OUTPUT_TYPES.find((o) => o.id === selected);
    setSelected(null);
    setIsGenerateOpen(false);
  };

  return (
    <Dialog open={isGenerateOpen} onOpenChange={handleOpenChange}>
      <DialogContent className="gap-0 overflow-hidden rounded-xl border-border bg-card p-0 shadow-lg sm:max-w-xl">
        <div className="border-b border-border bg-background/40 px-6 pt-6 pb-4">
          <DialogHeader className="space-y-0 text-left">
            <DialogTitle className="pr-8 text-lg font-semibold leading-tight tracking-tight">
              Generate Output
            </DialogTitle>
            <DialogDescription className="mt-2 text-sm leading-relaxed">
              {selectedHub
                ? `Choose an output type to generate for "${selectedHub.name}"`
                : "Select an output type to generate"}
            </DialogDescription>
          </DialogHeader>
        </div>

        <div className="bg-muted/15 px-6 py-5">
          <div className="grid grid-cols-2 gap-3">
            {OUTPUT_TYPES.map((output) => {
              const isSelected = selected === output.id;
              return (
                <Button
                  key={output.id}
                  type="button"
                  variant="outline"
                  onClick={() => setSelected(output.id)}
                  className={cn(
                    "relative h-auto min-h-[6.5rem] w-full flex-col items-stretch justify-start gap-3 rounded-xl border p-4 text-left shadow-none",
                    "whitespace-normal transition-colors duration-150",
                    "focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-card",
                    "border-border bg-card hover:bg-accent/40 hover:text-foreground",
                    isSelected && "bg-primary/10 hover:bg-primary/15",
                  )}
                >
                  <div className="flex w-full items-start justify-between gap-2">
                    <output.icon
                      className={cn(
                        "size-5 shrink-0 transition-colors",
                        isSelected ? "text-primary" : "text-muted-foreground",
                      )}
                    />
                    <CheckCircle2
                      className={cn("size-4 shrink-0 text-primary", !isSelected && "invisible")}
                      aria-hidden={!isSelected}
                    />
                  </div>
                  <div className="min-w-0 flex-1">
                    <p className="text-sm font-semibold text-foreground">{output.label}</p>
                    <p className="mt-1 text-xs leading-relaxed text-muted-foreground">{output.description}</p>
                  </div>
                </Button>
              );
            })}
          </div>
        </div>

        <DialogFooter className="gap-2 border-t border-border bg-background/40 px-6 py-4 sm:justify-end">
          <Button type="button" variant="outline" onClick={() => handleOpenChange(false)}>
            Cancel
          </Button>
          <Button type="button" disabled={!selected} onClick={handleGenerate}>
            Continue
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
